/** The Evidence panel: a browseable table of contents of what a diagnose run
 *  assembled, grouped the two ways an operator asks about it —
 *
 *    "was this in front of the model, or did it have to go and fetch it?"  (origin)
 *    "what kind of record did it come from?"                               (group)
 *
 *  Order is priority: the builders emit sections highest-value first and the
 *  budget is enforced by cutting from the tail, so "the last group is the one
 *  that got truncated" is a real diagnostic signal. Never re-sort the rows. */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  diagnoseContextApi,
  type DiagnoseContextEntry,
  type DiagnoseContextManifest,
} from '@/api/diagnoseContext';
import type { Tenant } from '@/utils/gridLinks';
import { formatBytes } from '@/utils/formatBytes';

import { KindIcon } from '../attachments/kind';
import { SectionDrawer } from './SectionDrawer';

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  ok: 'success',
  empty: 'default',
  pending: 'info',
  unsupported: 'default',
  unavailable: 'warning',
  expired: 'warning',
  error: 'error',
};

const REASON_TEXT: Record<string, string> = {
  expired: 'The captured evidence has aged out of the debug bucket. The run and its answer remain.',
  not_captured: 'This run stored no evidence bundle.',
  pending: 'The run has not finished assembling its evidence yet.',
};

function chars(n: number | null): string {
  if (n == null) return '—';
  if (n < 1000) return `${n}`;
  return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
}

function EntryRow({
  entry,
  onOpen,
}: {
  entry: DiagnoseContextEntry;
  onOpen: (e: DiagnoseContextEntry) => void;
}) {
  const att = entry.attachment ?? null;
  // An attached file always opens: preview, text, or at least its download.
  const clickable = att != null || entry.has_text || entry.refs.length > 0 || Boolean(entry.plan_summary);
  return (
    <Box
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onOpen(entry) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(entry);
              }
            }
          : undefined
      }
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1,
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {att ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 160 }}>
            <KindIcon kind={att.kind} contentType={att.content_type} fontSize="small" color="action" />
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {att.filename}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 160 }}>
            {entry.name}
          </Typography>
        )}
        <Chip size="small" label={entry.status} color={STATUS_COLOR[entry.status] ?? 'default'} variant="outlined" />
        <Typography variant="caption" color="text.secondary">
          {att ? `${formatBytes(att.size)} · turn ${att.turn}` : chars(entry.chars)}
          {!att && entry.pages > 1 ? ` · ${entry.pages} pages` : ''}
        </Typography>
        {entry.truncated_chars > 0 && (
          <Tooltip title={`${entry.truncated_chars} characters were cut to fit the budget — the model never saw them.`}>
            <Chip size="small" color="warning" label={`−${chars(entry.truncated_chars)} truncated`} />
          </Tooltip>
        )}
        {entry.origin === 'tool' && !entry.read && (
          <Chip size="small" variant="outlined" label="not opened" />
        )}
        {entry.refs.length > 0 && (
          <Chip size="small" variant="outlined" label={`${entry.refs.length} record${entry.refs.length > 1 ? 's' : ''}`} />
        )}
      </Stack>
      {(att ? entry.summary : entry.title || entry.summary) && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 0.5 }}>
          {att ? entry.summary : entry.title || entry.summary}
        </Typography>
      )}
      {entry.note && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ ml: 0.5 }}>
          {entry.note}
        </Typography>
      )}
    </Box>
  );
}

function OriginBlock({
  title,
  caption,
  entries,
  groups,
  onOpen,
}: {
  title: string;
  caption: string;
  entries: DiagnoseContextEntry[];
  groups: { key: string; title: string }[];
  onOpen: (e: DiagnoseContextEntry) => void;
}) {
  if (entries.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {caption}
      </Typography>
      {groups.map((g) => {
        const rows = entries.filter((e) => e.group === g.key);
        if (rows.length === 0) return null;
        return (
          <Box key={g.key} sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary">
              {g.title}
            </Typography>
            {rows.map((e) => (
              <EntryRow key={`${e.origin}:${e.name}`} entry={e} onOpen={onOpen} />
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

export function ContextPanel({ taskId, tenant }: { taskId: string | null; tenant?: Tenant }) {
  const [open, setOpen] = useState<DiagnoseContextEntry | null>(null);

  const query = useQuery<DiagnoseContextManifest>({
    queryKey: ['ai-diag', 'context', taskId],
    queryFn: () => diagnoseContextApi.manifest(taskId as string),
    enabled: Boolean(taskId),
    staleTime: Infinity,
  });

  if (!taskId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Run a diagnosis or a context preview to see the evidence that was assembled.
      </Typography>
    );
  }
  if (query.isLoading) return <CircularProgress size={20} />;
  if (query.isError) {
    return <Alert severity="error">The evidence for this run could not be loaded.</Alert>;
  }

  const m = query.data;
  if (!m || !m.available) {
    return <Alert severity="info">{REASON_TEXT[m?.reason ?? ''] ?? 'No evidence is available for this run.'}</Alert>;
  }

  const prompt = m.entries.filter((e) => e.origin === 'prompt');
  const viaTool = [...m.entries.filter((e) => e.origin === 'tool'), ...m.tools];

  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Chip size="small" label={`${m.totals.sections} sections`} />
        <Chip size="small" label={`${chars(m.totals.prompt_chars)} in the prompt`} />
        {m.totals.delivered_chars != null && (
          <Chip size="small" label={`${chars(m.totals.delivered_chars)} read`} />
        )}
        {m.totals.tool_calls != null && <Chip size="small" label={`${m.totals.tool_calls} tool calls`} />}
        {m.preview && <Chip size="small" color="info" label="context preview (no model call)" />}
        {m.context_source === 'rebuilt' && (
          <Tooltip title="The thread's captured evidence was gone, so this turn re-gathered it — it may differ from what earlier answers saw.">
            <Chip size="small" color="warning" label="context re-captured" />
          </Tooltip>
        )}
      </Stack>
      {m.totals.truncated_chars > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {chars(m.totals.truncated_chars)} characters were cut to fit the context budget. Sections
          are listed in priority order and the budget is enforced from the bottom, so the lowest
          entries lost the most.
        </Alert>
      )}
      <Divider sx={{ mb: 1 }} />

      <OriginBlock
        title="In the initial prompt"
        caption="The model was shown this without asking."
        entries={prompt}
        groups={m.groups}
        onOpen={setOpen}
      />
      <OriginBlock
        title="Available via tool"
        caption={
          m.architecture === 'bundle'
            ? 'Assembled and frozen, but only read if the model chose to open it.'
            : 'Not pre-collected — the model could fetch these during the run.'
        }
        entries={viaTool}
        groups={m.groups}
        onOpen={setOpen}
      />

      <SectionDrawer
        taskId={taskId}
        entry={open}
        tenant={tenant}
        onClose={() => setOpen(null)}
      />
    </Box>
  );
}
